import { config, gameState, resetGameState } from '@js/state';
import { PASS_THRESHOLD } from '@utils/roundGenerator';

/**
 * EndScene — reward reveal.
 *
 * Direct port of games/truck-loading/src/js/scenes/EndScene.js, which itself
 * ports buzzer-frizer's spotlight + reward-reveal sequence:
 *   • reward-bg backdrop, end-overlay dim wash, spotlight cone fades in
 *   • baseCircle plays "circleopen" then loops "circleglitterloop"
 *   • rewardLight plays "rewardopen" once
 *   • the reward (pizza-cutter) starts off-centre below, scaled 0.3, angled 300°
 *     and tweens up to centre at scale 1, angle 0
 *   • glitter sprite orbits the reward in an elliptical curve
 * Pizza-game layer on top: "PIZZA CUTTER" caption + Play-again CTA pill.
 */
let rewardTriggered = false;

export default class EndScene extends Phaser.Scene {
  constructor() {
    super({ key: 'EndScene' });
  }

  create() {
    rewardTriggered = false;
    const { width: W, height: H } = config;
    this.passed = gameState.score >= PASS_THRESHOLD;

    this.cameras.main.fadeIn(400, 0, 0, 0);

    // ─── Shared staging: bg + dim + spotlight cone (passed AND game-over) ──
    this.cameras.main.setBackgroundColor('#D6F5FF');
    if (this.textures.exists('reward-bg')) {
      const bg = this.add.image(W / 2, H / 2, 'reward-bg');
      const sx = W / (bg.width || 1);
      const sy = H / (bg.height || 1);
      bg.setScale(Math.max(sx, sy));
    }

    if (this.textures.exists('end-overlay')) {
      this.endOverlay = this.add
        .image(W / 2, H / 2, 'end-overlay')
        .setDisplaySize(W, H)
        .setAlpha(0.7);
    }

    if (this.textures.exists('spotlight-cone')) {
      this.spotlightCone = this.add
        .image(W / 2, 0, 'spotlight-cone')
        .setOrigin(0.5, 0)
        .setDisplaySize(720, 717)
        .setAlpha(0);
      this.tweens.add({
        targets: this.spotlightCone,
        alpha: 1,
        duration: 1200,
        delay: 1500,
        ease: 'Sine.easeOut',
      });
    }

    if (this.passed) {
      // Full reward reveal.
      this.buildConfetti(W, H);
      if (this.textures.exists('rewards-earned')) {
        this.rewardsEarned = this.add
          .image(W / 2, H / 2, 'rewards-earned')
          .setAlpha(0);
        this.tweens.add({
          targets: this.rewardsEarned,
          ease: 'Expo.easeInOut',
          duration: 1000,
          delay: 300,
          alpha: 1,
        });
        this.tweens.add({
          targets: this.rewardsEarned,
          ease: 'Linear',
          duration: 300,
          delay: 1800,
          y: H / 2 - 250,
        });
      }
      this.time.delayedCall(2800, () => this.runRewardSequence());
    } else {
      // Game-over variant: same staging (bg+dim+spotlight) without the
      // celebration. "GAME OVER" appears in the cone, Play Again below.
      this.time.delayedCall(2800, () => this.runGameOverSequence());
    }
  }

  // ─────────────────────────── game-over sequence ─────────────────────────
  runGameOverSequence() {
    const W = config.width;
    const H = config.height;

    // Figma node 1829:115 — Desktop Headlines/H1 (Athletics Light 60px white).
    this.gameOverText = this.add
      .text(W / 2, H / 2 - 40, 'Game Over', {
        fontFamily: 'Athletics',
        fontSize: '60px',
        fontStyle: '300',
        color: '#FFFFFF',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({
      targets: this.gameOverText,
      alpha: 1, duration: 600, ease: 'Sine.easeOut',
    });

    this.buildEndCtas();
  }

  buildConfetti(W, H) {
    if (!this.textures.exists('endConfetti')) return;
    this.endConfetti = this.add.sprite(W / 2, H / 2, 'endConfetti');
    if (!this.anims.exists('endConfetti')) {
      this.anims.create({
        key: 'endConfetti',
        frames: this.anims.generateFrameNames('endConfetti', {
          start: 0, end: 120, prefix: 'seq_0_', suffix: '.png',
        }),
        frameRate: 40,
        repeat: 0,
      });
    }
  }

  // ─────────────────────── reward reveal sequence ───────────────────────
  runRewardSequence() {
    const W = config.width;
    const H = config.height;
    const midWidth = W / 2;
    const midHeight = H / 2;

    this.yOffset = 75;
    // The pizza-cutter sprite has its wheel head at the TOP and handle at
    // the bottom; sprite origin is the centre, so the wheel head sits about
    // 80px above the sprite centre at the rendered 0.5 scale. Offset the
    // glitter orbit upward by that amount so the sparkle circles the head,
    // not the handle.
    const HEAD_OFFSET_Y = 80;
    this.centerxOfCircle = midWidth;
    this.centeryOfCircle = (midHeight - this.yOffset) - HEAD_OFFSET_Y;
    this.rewardAppearDelay = 1300;
    this.rewardMotionDuration = 1350;

    this.baseCircle = this.add
      .sprite(midWidth, midHeight, 'baseCircle')
      .setOrigin(0.5)
      .setScale(0.4);

    this.rewardLight = this.add
      .sprite(midWidth, midHeight, 'baseCircle')
      .setOrigin(0.5)
      .setScale(0.4);

    // Reward — pizza-cutter. The Figma asset was reduced to its target
    // size and exported at 2×; render at 0.5 scale to match Figma at 1×.
    this.reward = this.add
      .image(midWidth, midHeight + 250 - 120, 'pizza-cutter')
      .setOrigin(0.5)
      .setVisible(false)
      .setAngle(300)
      .setScale(0.5);
    this.rewardTargetScaleX = this.reward.scaleX;
    this.rewardTargetScaleY = this.reward.scaleY;
    this.reward.setScale(this.rewardTargetScaleX * 0.3, this.rewardTargetScaleY * 0.3);

    this.glitter = this.add
      .sprite(midWidth + 30, midHeight - 65, 'glitter')
      .setOrigin(0.5)
      .setVisible(false)
      .setScale(0.8);

    this.path = { t: 0, vec: new Phaser.Math.Vector2() };
    // Orbit radius matches the wheel head (~50px wide at 0.5 scale display).
    this.curve = new Phaser.Curves.Ellipse(
      this.centerxOfCircle,
      this.centeryOfCircle,
      30,
      30,
    );
    this.tweens.add({
      targets: this.path,
      t: 1,
      ease: 'Sine.easeInOut',
      duration: 5400,
      yoyo: true,
      repeat: -1,
    });

    this.time.delayedCall(this.rewardAppearDelay, () => {
      this.reward.setVisible(true);
    });
    this.tweens.add({
      targets: this.reward,
      x: midWidth,
      y: midHeight - this.yOffset,
      angle: 0,
      scaleX: this.rewardTargetScaleX,
      scaleY: this.rewardTargetScaleY,
      delay: this.rewardAppearDelay,
      ease: 'Linear',
      duration: 1000,
      onComplete: () => {
        this.glitter.setVisible(true).play('showglitter');
        rewardTriggered = true;

        this.tweens.add({
          targets: this.curve.p0,
          y: this.centeryOfCircle + 70,
          ease: 'Sine.easeInOut',
          duration: this.rewardMotionDuration,
          yoyo: true,
          repeat: -1,
        });
        this.tweens.add({
          targets: this.reward,
          y: { from: midHeight - this.yOffset, to: midHeight + 70 - this.yOffset },
          ease: 'Sine.easeInOut',
          duration: this.rewardMotionDuration,
          yoyo: true,
          repeat: -1,
        });

        this.buildEndCaption();
        this.buildEndCtas();
      },
    });

    // Anims for the reward atlases.
    if (!this.anims.exists('showglitter')) {
      this.anims.create({
        key: 'showglitter',
        frames: this.anims.generateFrameNames('glitter', {
          start: 0, end: 32, prefix: 'glitterAnim', suffix: '.png',
        }),
        frameRate: 15,
        repeat: -1,
      });
    }
    if (!this.anims.exists('circleopen')) {
      this.anims.create({
        key: 'circleopen',
        frames: this.anims.generateFrameNames('baseCircle', {
          start: 0, end: 74, prefix: 'baseCircleAnim', suffix: '.png',
        }),
        frameRate: 30,
        repeat: 0,
      });
    }
    if (!this.anims.exists('circleglitterloop')) {
      this.anims.create({
        key: 'circleglitterloop',
        frames: this.anims.generateFrameNames('baseCircle', {
          start: 74, end: 155, prefix: 'baseCircleAnim', suffix: '.png',
        }),
        frameRate: 30,
        repeat: -1,
      });
    }
    if (!this.anims.exists('rewardopen')) {
      this.anims.create({
        key: 'rewardopen',
        frames: this.anims.generateFrameNames('rewardLight', {
          start: 0, end: 155, prefix: 'rewardLightAnim', suffix: '.png',
        }),
        frameRate: 30,
        repeat: 0,
      });
    }

    this.baseCircle.play('circleopen').once('animationcomplete', () => {
      this.baseCircle.play('circleglitterloop');
    });
    this.rewardLight.play('rewardopen');
    if (this.endConfetti) this.endConfetti.play('endConfetti');
  }

  update() {
    if (rewardTriggered && this.glitter && this.curve) {
      this.curve.getPoint(this.path.t, this.path.vec);
      this.glitter.setPosition(this.path.vec.x, this.path.vec.y);
    }
  }

  buildEndCaption() {
    const W = config.width;
    this.caption = this.add
      .text(W / 2, config.height / 2 + 195, 'PIZZA CUTTER', {
        fontFamily: 'Athletics',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#FFFFFF',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({ targets: this.caption, alpha: 1, duration: 400 });
  }

  buildEndCtas() {
    const W = config.width;
    this.cta = this.buildCtaButton(W / 2, 620, 'btn-play-again', () => this.replay());
    this.cta.bg.setAlpha(0);
    this.tweens.add({
      targets: [this.cta.bg],
      alpha: 1,
      duration: 400,
      delay: 200,
    });
  }

  buildCtaButton(x, y, key, onClick, opts = {}) {
    // ─── Pre-flight ───
    // Inputs: pointerdown/up on the button sprite.
    // State mutated: button fires onClick once and disables itself.
    // Re-entry guards: disableInteractive() at start of pointerup before firing.
    // Cleanup: sprite destroyed when scene shuts down.
    // Manually verified: [x] double click [x] click during scene fade [x] rapid clicks
    const hoverKey = `${key}-hover`;
    const h = opts.height || 48;

    const bg = this.add
      .image(x, y, key)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    const w = (bg.width / bg.height) * h;
    bg.setDisplaySize(w, h);

    const hasHoverTexture = this.textures.exists(hoverKey);
    bg.on('pointerover', () => {
      if (hasHoverTexture) bg.setTexture(hoverKey);
      bg.setDisplaySize(w, h);
    });
    bg.on('pointerout', () => {
      bg.setTexture(key);
      bg.setDisplaySize(w, h);
    });
    bg.on('pointerdown', () => bg.setDisplaySize(w * 0.97, h * 0.97));
    bg.on('pointerup', () => {
      bg.disableInteractive();
      bg.setDisplaySize(w, h);
      onClick();
    });

    return { bg };
  }

  replay() {
    resetGameState();
    this.cameras.main.fadeOut(150, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('StartScene');
    });
  }
}
